import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { YoutubeIcon } from 'components/icons/YoutubeIcon';
import { BrandWordmark } from 'components/brand/BrandWordmark';
import { landing } from 'constants/copy';
import { brandContact, siteFooterCopy } from 'constants/brandContact';
import { ROUTES } from 'constants/routes';
import { cn } from 'utils/cn';

const footerLinks = [
  { label: landing.footer.links.about, to: ROUTES.ABOUT },
  { label: landing.footer.links.contact, to: ROUTES.CONTACT },
  { label: landing.footer.links.privacy, to: ROUTES.PRIVACY },
  { label: landing.footer.links.terms, to: ROUTES.TERMS },
] as const;

export function SiteFooter({ className }: { className?: string }) {
  const year = new Date().getFullYear();

  return (
    <footer
      className={cn(
        'shrink-0 border-t border-border bg-surface-warm-secondary/40 dark:bg-surface-secondary/20',
        className
      )}
    >
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-col gap-1">
            <BrandWordmark />
          </div>

          <nav
            className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-secondary"
            aria-label="Footer"
          >
            {footerLinks.map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                className="transition-colors hover:text-accent-strong"
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`mailto:${brandContact.email}`}
              className="inline-flex min-h-[36px] max-w-full items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-content-secondary transition-colors hover:border-accent/30 hover:text-accent-strong"
            >
              <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{brandContact.email}</span>
            </a>
            <a
              href={brandContact.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${siteFooterCopy.youtubeChannel} ${brandContact.youtubeHandle}`}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg transition-opacity hover:opacity-90"
            >
              <YoutubeIcon className="h-7 w-7" />
            </a>
          </div>
        </div>

        <div className="mt-3 flex flex-col items-start justify-between gap-2 border-t border-border/60 pt-3 text-xs leading-relaxed text-content-tertiary sm:flex-row sm:items-center">
          <p className="max-w-3xl">{siteFooterCopy.copyright(year)}</p>
          <p className="shrink-0 font-devanagari" lang="hi">
            {siteFooterCopy.sanskrit}
          </p>
        </div>
      </div>
    </footer>
  );
}
